import { FC, useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import dayjs from 'dayjs';
import classNames from 'classnames';

import type { ScheduleProps } from './index';

import { Button, BackButton, ArrowButton, Heading, Pressable } from '../../../shared/ui';

import { getDate, getMonday, getTime } from '../../../utils/getTime';

const HOURS_IN_DAY = 24;
const VISIBLE_DAYS = 2;
const HOUR_HEIGHT = 48;
const DAYS_PAST = 30;
const DAYS_FUTURE = 120;

const getCurrentTimeOffset = () => {
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  return hours * HOUR_HEIGHT + (minutes * HOUR_HEIGHT) / 60;
};

export const ScheduleMobile: FC<ScheduleProps> = ({
  close,
  openCreateForm,
  clients,
  consultations,
  handleChangeCons,
  onCreateFromSlot,
}) => {
  const [consultationList, setConsultationList] = useState(consultations);
  const startDate = useMemo(() => dayjs(getMonday(new Date())).subtract(DAYS_PAST, 'day').toDate(), []);
  const days = useMemo(
    () => Array.from({ length: DAYS_PAST + DAYS_FUTURE }, (_, idx) => dayjs(startDate).add(idx, 'day').toDate()),
    [startDate],
  );
  const maxMobileStartIndex = Math.max(0, days.length - VISIBLE_DAYS);
  const clampIndex = (idx: number) => Math.min(Math.max(0, idx), maxMobileStartIndex);
  const todayIndex = days.findIndex((day) => dayjs(day).isSame(dayjs(), 'day'));
  const initialVisibleOffset = todayIndex === -1 ? clampIndex(DAYS_PAST) : clampIndex(todayIndex);
  const [visibleDayOffset, setVisibleDayOffset] = useState(initialVisibleOffset);
  const [currentTimeOffset, setCurrentTimeOffset] = useState(getCurrentTimeOffset());
  const [initialScrollApplied, setInitialScrollApplied] = useState(false);
  const dayRefs = useRef<(HTMLDivElement | null)[]>([]);
  const daysScrollRef = useRef<HTMLDivElement | null>(null);
  const skipNextScrollIntoView = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const scrollSyncFrame = useRef<number | null>(null);

  const visibleDays = useMemo(
    () => days.slice(visibleDayOffset, visibleDayOffset + VISIBLE_DAYS),
    [days, visibleDayOffset],
  );
  const headerMonthLabel = dayjs(visibleDays[0] ?? days[0]).format('MMMM YYYY');
  const isTodayVisible = visibleDays.some((day) => dayjs(day).isSame(dayjs(), 'day'));
  const shouldShowTodayButton = todayIndex !== -1 && !isTodayVisible;

  useEffect(() => {
    setConsultationList(consultations);
  }, [consultations]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTimeOffset(getCurrentTimeOffset());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  useLayoutEffect(() => {
    if (initialScrollApplied) return;
    const container = daysScrollRef.current;
    if (!container) return;
    const child = container.children[visibleDayOffset] as HTMLElement | undefined;
    if (child) {
      isProgrammaticScrollRef.current = true;
      child.scrollIntoView({ inline: 'center', block: 'nearest' });
      requestAnimationFrame(() => {
        isProgrammaticScrollRef.current = false;
      });
    }
    setInitialScrollApplied(true);
  }, [visibleDayOffset, initialScrollApplied]);

  useEffect(() => {
    if (!initialScrollApplied) return;
    if (skipNextScrollIntoView.current) {
      skipNextScrollIntoView.current = false;
      return;
    }
    const container = daysScrollRef.current;
    if (!container) return;
    const child = container.children[visibleDayOffset] as HTMLElement | undefined;
    if (child) {
      isProgrammaticScrollRef.current = true;
      child.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      requestAnimationFrame(() => {
        isProgrammaticScrollRef.current = false;
      });
    }
  }, [visibleDayOffset, initialScrollApplied]);

  const handleMonthChange = (direction: 1 | -1) => {
    const currentStart = dayjs(days[visibleDayOffset] ?? days[0]);
    const targetMonthStart = currentStart.add(direction, 'month').startOf('month');

    let targetIndex = days.findIndex((day) => dayjs(day).isSame(targetMonthStart, 'day'));

    if (targetIndex === -1) {
      targetIndex =
        direction > 0
          ? days.findIndex((day) => dayjs(day).isAfter(targetMonthStart))
          : (() => {
              const reverseIdx = [...days].reverse().findIndex((day) => dayjs(day).isBefore(targetMonthStart));
              return reverseIdx === -1 ? -1 : days.length - 1 - reverseIdx;
            })();
    }

    if (targetIndex === -1) {
      targetIndex = direction > 0 ? days.length - VISIBLE_DAYS : 0;
    }

    const clamped = clampIndex(targetIndex);
    setVisibleDayOffset(clamped);
  };

  const handleCreateFromPosition = (idx: number, clientY: number) => {
    const column = dayRefs.current[idx];
    if (!column) return;

    const rect = column.getBoundingClientRect();
    const relativeY = clientY - rect.top;
    const minutesPerPixel = 60 / HOUR_HEIGHT;
    let minutes = Math.round((relativeY * minutesPerPixel) / 30) * 30;
    const durationMinutes = 60;
    const minutesInDay = 24 * 60;
    minutes = Math.max(0, minutes);
    const maxStart = minutesInDay - durationMinutes;
    if (minutes > maxStart) {
      minutes = maxStart;
    }

    const dayValue = visibleDays[idx];
    if (!dayValue) return;
    const dayDate = dayjs(new Date(dayValue));
    const start = dayDate.startOf('day').add(minutes, 'minute');
    const end = start.add(durationMinutes, 'minute');

    onCreateFromSlot({
      date: start.startOf('day').toISOString(),
      start: start.format('HH:mm'),
      end: end.format('HH:mm'),
    });
  };

  const renderTimeLabels = () =>
    Array.from({ length: HOURS_IN_DAY }, (_, hour) => (
      <div
        key={`time-label-${hour}`}
        className="h-12 flex items-center justify-end pr-2 text-ui-xs text-ink-subtle"
        style={{ height: HOUR_HEIGHT }}
      >
        {`${hour.toString().padStart(2, '0')}:00`}
      </div>
    ));

  const renderGridLines = () =>
    Array.from({ length: HOURS_IN_DAY }, (_, hour) => (
      <div
        key={`grid-line-${hour}`}
        className="h-12 border-b border-dashed border-line"
        style={{ height: HOUR_HEIGHT }}
      />
    ));

  const handlePrevMonth = () => handleMonthChange(-1);
  const handleNextMonth = () => handleMonthChange(1);

  const handleDayClick = (idx: number) => {
    const clamped = clampIndex(idx);
    setVisibleDayOffset(clamped);
  };

  const handleGoToToday = () => {
    if (todayIndex === -1) return;
    const targetIndex = clampIndex(todayIndex);
    setVisibleDayOffset(targetIndex);
  };

  useEffect(() => {
    return () => {
      if (scrollSyncFrame.current !== null) {
        cancelAnimationFrame(scrollSyncFrame.current);
      }
    };
  }, []);

  const handleDaysScroll = () => {
    if (isProgrammaticScrollRef.current) return;
    if (scrollSyncFrame.current !== null) return;

    scrollSyncFrame.current = requestAnimationFrame(() => {
      scrollSyncFrame.current = null;
      const container = daysScrollRef.current;
      if (!container) return;
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;
      const children = Array.from(container.children) as HTMLElement[];
      if (!children.length) return;

      let closestIdx = 0;
      let minDistance = Number.POSITIVE_INFINITY;

      children.forEach((child, idx) => {
        const rect = child.getBoundingClientRect();
        const childCenter = rect.left + rect.width / 2;
        const distance = Math.abs(childCenter - containerCenter);
        if (distance < minDistance) {
          minDistance = distance;
          closestIdx = idx;
        }
      });

      const clamped = clampIndex(closestIdx);
      if (clamped !== visibleDayOffset) {
        skipNextScrollIntoView.current = true;
        setVisibleDayOffset(clamped);
      }
    });
  };

  dayRefs.current = [];

  return (
    <div className="flex flex-col h-full rounded-card bg-white shadow-raised-xl overflow-hidden">
      <header className="border-b border-surface-muted px-4 py-3 flex items-center justify-between">
        <BackButton className="m-1.5" onClick={close} />
        <Heading size="xl">Календарь</Heading>
        <span className="w-10 h-10" aria-hidden="true" />
      </header>
      <div className="px-4 py-3 border-b border-surface-muted flex flex-col gap-3">
        <div className="flex items-center justify-between text-ui text-ink-subtle">
          <ArrowButton direction="prev" label="Предыдущий месяц" onClick={handlePrevMonth} />
          <div className="flex items-center gap-3">
            {shouldShowTodayButton && (
              <Button size="sm" variant="neutral" onClick={handleGoToToday}>
                Сегодня
              </Button>
            )}
            <p className="uppercase tracking-wide font-medium">{headerMonthLabel}</p>
          </div>
          <ArrowButton direction="next" label="Следующий месяц" onClick={handleNextMonth} />
        </div>
        <div className="relative">
          <div
            ref={daysScrollRef}
            onScroll={handleDaysScroll}
            className="flex gap-2 overflow-x-auto no-scrollbar border border-line rounded-card p-1 bg-surface-muted"
          >
            {days.map((day, idx) => {
              const isActive = idx >= visibleDayOffset && idx < visibleDayOffset + VISIBLE_DAYS;
              return (
                <Pressable
                  onClick={() => handleDayClick(idx)}
                  key={dayjs(day).format('YYYY-MM-DD')}
                  className={classNames(
                    'flex flex-col items-center justify-center py-2 px-3 gap-1 min-h-[64px] rounded-control transition-colors min-w-[56px]',
                    {
                      'bg-white text-ink shadow-raised': isActive,
                      'bg-transparent text-ink-subtle': !isActive,
                    },
                  )}
                >
                  <span className="text-ui-xs">{dayjs(day).format('dd')}</span>
                  <span className="text-heading-md">{dayjs(day).format('DD')}</span>
                </Pressable>
              );
            })}
          </div>
        </div>
      </div>
      <main className="flex-1 overflow-hidden">
        <div className="h-[calc(100vh_-_260px)] overflow-y-auto">
          <div className="flex items-stretch">
            <div className="w-[64px] shrink-0 text-right bg-white">{renderTimeLabels()}</div>
            <div className="flex-1 grid grid-cols-2 border-l border-line">
              {visibleDays.map((day, idx) => {
                const dayKey = dayjs(day).format('YYYY-MM-DD');
                return (
                  <div
                    key={dayKey}
                    ref={(node) => {
                      dayRefs.current[idx] = node;
                    }}
                    className="relative border-l border-line bg-surface"
                    style={{ minHeight: HOURS_IN_DAY * HOUR_HEIGHT }}
                    onClick={(event) => {
                      const target = event.target as HTMLElement;
                      if (target.closest('[data-consultation-card="true"]')) {
                        return;
                      }
                      handleCreateFromPosition(idx, event.clientY);
                    }}
                  >
                    <div className="absolute inset-0 pointer-events-none">{renderGridLines()}</div>
                    <div
                      className="absolute left-0 right-0 border-t border-dashed border-primary pointer-events-none"
                      style={{ top: `${currentTimeOffset}px` }}
                    />
                    {consultationList
                      .filter((item) => getDate(new Date(item.date)) === getDate(new Date(day)))
                      .map((consul) => {
                        const client = clients.find((el) => `/api/v1/clients/${el.id}` === consul.client_entity);
                        const start = getTime(new Date(consul.datetime_start)).split(':');
                        const end = getTime(new Date(consul.datetime_end)).split(':');
                        const startMinutes = Number(start[0]) * 60 + Number(start[1]);
                        const endMinutes = Number(end[0]) * 60 + Number(end[1]);
                        const duration = Math.max(endMinutes - startMinutes, 30);
                        const title = client?.full_name ?? 'Неизвестный клиент';

                        return (
                          <div
                            key={consul.id}
                            data-consultation-card="true"
                            className="absolute left-0.5 right-0.5 cursor-pointer rounded-chip bg-accent text-ink text-ui-xs-strong px-3 py-2 shadow-raised"
                            style={{
                              top: `${(startMinutes / 60) * HOUR_HEIGHT}px`,
                              height: `${(duration / 60) * HOUR_HEIGHT - 6}px`,
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleChangeCons(consul.id);
                            }}
                            title={title}
                          >
                            <p className="truncate">{title}</p>
                            <p className="text-micro text-ink-subtle mt-1">
                              {start.join(':')} - {end.join(':')}
                            </p>
                          </div>
                        );
                      })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>
      <Button
        size="lg"
        iconOnly
        variant="primary"
        onClick={openCreateForm}
        className="fixed bottom-5 right-10 z-20 shadow-popup"
        aria-label="Создать встречу"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 5V19M5 12H19"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Button>
    </div>
  );
};
